import { useSEO } from '../../hooks/useSEO';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://yourmindshift24.com';

export default function ImpressumPage() {
  useSEO({
    title: 'Impressum – YourMindShift24 | Future Now UG, Aschheim',
    description: 'Impressum von YourMindShift24. Verantwortlich: Future Now UG (haftungsbeschränkt), Max-Planck-Str. 4, 85609 Aschheim. HRB 277509, Amtsgericht München.',
    keywords: 'Impressum YourMindShift24, Future Now UG, Olaf Niggemann, Aschheim',
    canonical: '/impressum',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Impressum – YourMindShift24',
      url: `${SITE_URL}/impressum`,
      description: 'Impressum von YourMindShift24 – Future Now UG, Aschheim',
      publisher: {
        '@type': 'Organization',
        name: 'Future Now UG',
        url: SITE_URL,
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Max-Planck-Str. 4',
          addressLocality: 'Aschheim',
          postalCode: '85609',
          addressCountry: 'DE',
        },
        legalName: 'Future Now UG (haftungsbeschränkt)',
        vatID: 'DE355267875',
      },
      inLanguage: 'de-DE',
      dateModified: '2026-03-27',
    },
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="prose prose-lg max-w-none">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">Impressum</h1>
          
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Angaben gemäß § 5 TMG</h2>
            <div className="bg-teal-50 p-6 rounded-lg">
              <p className="text-gray-700 leading-relaxed text-lg">
                <strong>Future Now UG (haftungsbeschränkt)</strong><br />
                Max-Planck-Str. 4<br />
                85609 Aschheim<br />
                Deutschland
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Vertreten durch</h2>
            <p className="text-gray-700 leading-relaxed text-lg">
              <strong>Geschäftsführer:</strong> Olaf Niggemann
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Kontakt</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-gray-800 mb-2">Postanschrift</h3>
                <p className="text-gray-700">
                  Future Now UG<br />
                  Max-Planck-Str. 4<br />
                  85609 Aschheim<br />
                  Deutschland
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-gray-800 mb-2">Digital</h3>
                <p className="text-gray-700">
                  <strong>Telegram:</strong> @mindshift_ai_bot<br />
                  <strong>Website:</strong> mindshift-ai.de
                </p>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Registereintrag</h2>
            <p className="text-gray-700 leading-relaxed">
              <strong>Eintragung im Handelsregister:</strong><br />
              Registergericht: Amtsgericht München<br />
              Registernummer: HRB 277509
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Umsatzsteuer-ID</h2>
            <p className="text-gray-700 leading-relaxed">
              Umsatzsteuer-Identifikationsnummer gemäß § 27 a Umsatzsteuergesetz:<br />
              DE355267875
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Redaktionell verantwortlich</h2>
            <p className="text-gray-700 leading-relaxed">
              Olaf Niggemann<br />
              Future Now UG<br />
              Max-Planck-Str. 4<br />
              85609 Aschheim
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">EU-Streitschlichtung</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: 
              <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noopener noreferrer" 
                 className="text-teal-600 hover:text-teal-700 underline ml-1">
                https://ec.europa.eu/consumers/odr/
              </a>
            </p>
            <p className="text-gray-700 leading-relaxed">
              Unsere E-Mail-Adresse finden Sie oben im Impressum.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Verbraucherstreitbeilegung/Universalschlichtungsstelle</h2>
            <p className="text-gray-700 leading-relaxed">
              Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer 
              Verbraucherschlichtungsstelle teilzunehmen.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Haftung für Inhalte</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den 
              allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht 
              unter der Verpflichtung, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach 
              Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den allgemeinen 
              Gesetzen bleiben hiervon unberührt. Eine diesbezügliche Haftung ist jedoch erst ab dem Zeitpunkt 
              der Kenntnis einer konkreten Rechtsverletzung möglich.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Haftung für Links</h2>
            <p className="text-gray-700 leading-relaxed">
              Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. 
              Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der 
              verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Urheberrecht</h2>
            <p className="text-gray-700 leading-relaxed">
              Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem 
              deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung 
              außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen 
              Autors bzw. Erstellers.
            </p>
          </section>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
